/**
 * Keyframe decimation for sampled animation.
 *
 * A baked import keys every frame: a 2-second Mixamo clip at 30 fps is 60 frames x 18 bones =
 * 1080 keyframes. That is faithful and unusable — the dope sheet fills up, the easing system
 * becomes meaningless (every frame is already a key) and hand-editing is hopeless.
 *
 * This is Douglas-Peucker on the rotation curve rather than a greedy neighbour pass: the key
 * that deviates most from the straight slerp between the segment's endpoints is the one kept, so
 * a fast-moving stretch keeps its detail while a slow one collapses to two keys. The error bound
 * is global for the segment, not local to a triple, which a neighbour pass cannot promise.
 *
 * A Hips track may also carry `hipsY`. That curve is judged in the same pass against its own
 * tolerance and the two errors are combined as a ratio, so a crouch whose rotations happen to be
 * flat still keeps the key at the bottom of the dip.
 *
 * Pure: tuples in, tuples out.
 */

import { angleBetween, slerpTuple } from '../shared';
import type { RotationKeyframe } from '../shared';

/** Default angular tolerance in degrees — visually lossless on human motion. */
export const DEFAULT_DECIMATION_DEGREES = 0.5;

/**
 * Default tolerance for the `hipsY` channel: 0.5 % of leg length, i.e. ~4 mm on a 0.85 m leg.
 * `hipsY` is dimensionless, so this is the same tolerance on every rig.
 */
export const DEFAULT_HIPS_Y_TOLERANCE = 0.005;

const scratch: [number, number, number, number] = [0, 0, 0, 1];

/**
 * Worst per-key error inside `[from, to]`, as a RATIO of the tolerance it was measured against, so
 * the rotation curve and the `hipsY` curve can be judged by one number: a key is worth keeping
 * when the ratio exceeds 1, whichever channel is responsible.
 *
 * Rotation error is the angle to the slerp across the span; `hipsY` error is the distance to the
 * straight line between the endpoints' `hipsY`. A key or an endpoint without the field scores 0
 * on that channel — a track that never carried `hipsY` therefore decimates exactly as before.
 *
 * Returns the index of the worst key, or -1 when the span has no interior.
 */
function worstDeviation(
  keys: readonly RotationKeyframe[],
  from: number,
  to: number,
  toleranceRadians: number,
  hipsYTolerance: number,
): { index: number; error: number } {
  const start = keys[from];
  const end = keys[to];
  const span = end.frame - start.frame;

  const startY = start.hipsY;
  const endY = end.hipsY;
  const testHipsY =
    hipsYTolerance > 0 && typeof startY === 'number' && typeof endY === 'number';

  let index = -1;
  let error = 0;

  for (let i = from + 1; i < to; i++) {
    const key = keys[i];
    const t = span === 0 ? 0 : (key.frame - start.frame) / span;
    slerpTuple(start.rotation, end.rotation, t, scratch);
    let ratio = angleBetween(scratch, key.rotation) / toleranceRadians;

    const y = key.hipsY;
    if (testHipsY && typeof y === 'number') {
      const straight = startY! + (endY! - startY!) * t;
      ratio = Math.max(ratio, Math.abs(y - straight) / hipsYTolerance);
    }

    if (ratio > error) {
      error = ratio;
      index = i;
    }
  }

  return { index, error };
}

/**
 * Drop keyframes the curve does not need.
 *
 * The first and last key are always kept, so the clip's length and its endpoints never move.
 * A tolerance of 0 (or less) keeps every key, which is the escape hatch when a bake has to be
 * exact — and it short-circuits the `hipsY` test too, so an exact bake stays exact on both
 * channels.
 */
export function decimateKeyframes(
  keys: readonly RotationKeyframe[],
  toleranceDegrees: number = DEFAULT_DECIMATION_DEGREES,
  hipsYTolerance: number = DEFAULT_HIPS_Y_TOLERANCE,
): RotationKeyframe[] {
  if (keys.length <= 2 || toleranceDegrees <= 0) return keys.map((k) => ({ ...k }));

  const tolerance = (toleranceDegrees * Math.PI) / 180;
  const keep = new Uint8Array(keys.length);
  keep[0] = 1;
  keep[keys.length - 1] = 1;

  // Explicit stack: a 3000-frame clip would otherwise be able to blow the call stack.
  const stack: [number, number][] = [[0, keys.length - 1]];
  while (stack.length > 0) {
    const [from, to] = stack.pop()!;
    if (to - from < 2) continue;
    const { index, error } = worstDeviation(keys, from, to, tolerance, hipsYTolerance);
    // `error` is a ratio of its own tolerance, so the threshold is 1 for both channels at once.
    if (index < 0 || error <= 1) continue;
    keep[index] = 1;
    stack.push([from, index], [index, to]);
  }

  const out: RotationKeyframe[] = [];
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (keep[i]) out.push({ ...key, rotation: [...key.rotation] });
  }
  return out;
}
